import { NotificationButton } from '@avanta/shared/components/chat/NotificationButton';
import { SUPPORTER_ID } from '@avanta/shared/constants/chat';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import {
  useConversationsStore,
  useUnreadConversations,
} from '@avanta/shared/stores/chat/conversations';
import { cn } from '@avanta/shared/utils/cn';
import { Headset, MessagesSquare } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';

const NOTIFICATION_ICONS = {
  [String(SUPPORTER_ID)]: {
    icon: Headset,
    label: 'Notification.support',
  },
};

const DEFAULT_NOTIFICATION_ICON = {
  icon: MessagesSquare,
  label: 'Notification.Psychologist',
};

export const Notifications = (): React.ReactElement | null => {
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const location = useLocation()?.pathname;
  const isChatRoute = location === ROUTES.CHAT;
  const unreadCounts = useUnreadConversations();
  const { t } = useTranslation(LOCIZE_NS.CHAT);
  const createConversation = useConversationsStore(state => state.createConversation);
  const navigate = useNavigate();

  const handleClick = async (userId: string): Promise<void> => {
    try {
      setLoadingUserId(userId);
      await createConversation(userId);
      navigate(ROUTES.CHAT);
    } finally {
      setLoadingUserId(null);
    }
  };

  if (isChatRoute) return null;

  return (
    <div className="fixed bottom-4 right-0 px-4 flex items-end flex-col gap-2.5 animate-fade-in z-50">
      {unreadCounts.map(({ id, name, userId }) => {
        const { icon: Icon, label } = NOTIFICATION_ICONS[userId] ?? DEFAULT_NOTIFICATION_ICON;
        const buttonLabel = t(label, { name });
        const isLoading = loadingUserId === userId;
        return (
          <NotificationButton
            key={id}
            variant="primary"
            size="lg"
            icon="leading"
            onClick={() => handleClick(userId)}
            isLoading={isLoading}
            notificationUserId={userId}
            className={cn(
              'bg-plum-500 text-white pl-3 pr-5 rounded-xl relative gap-2 hover:bg-plum-700 hover:border-transparent disabled:bg-plum-100',
              isLoading && 'min-w-20'
            )}
          >
            <Icon className="size-6" />
            {buttonLabel}
          </NotificationButton>
        );
      })}
    </div>
  );
};
